import React from 'react';
import { styled } from 'styled-components';

const TooltipContent = styled.div`
  max-width: 24rem;
  line-height: 1.4;
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const TooltipParagraph = styled.p`
  margin: 0;

  & + & {
    margin-top: 0.4rem;
  }
`;

const TooltipList = styled.ul`
  margin: 0.35rem 0 0;
  padding-left: 1.15rem;
`;

const TooltipListItem = styled.li`
  margin: 0.2rem 0;
`;

interface TextTooltipProps {
  children: React.ReactNode;
  title?: string;
}

const TextTooltip: React.FC<TextTooltipProps> = ({ children, title }) => (
  <TooltipContent>
    {title && <TooltipTitle>{title}</TooltipTitle>}
    <TooltipParagraph>{children}</TooltipParagraph>
  </TooltipContent>
);

export const asoTooltips = {
  useMinRedistributionQuantity: (
    <TextTooltip title="Minimum redistribution quantity">
      Uses SKU-level minimum redistribution quantities from data for source SKUs when present.
      Missing or zero values fall back to the ASO setup default of 1.
    </TextTooltip>
  ),

  ignoreMinRedistributionQtyAndSetWhenDepletingToZero: (
    <TextTooltip title="Depleting to zero">
      Allows a source SKU to be emptied even when the remaining stock is below the minimum
      redistribution quantity or does not fit the redistribution set. Applies only when the source
      can be depleted down to zero.
    </TextTooltip>
  ),

  useSets: (
    <TooltipContent>
      <TooltipTitle>Redistribution sets</TooltipTitle>
      <TooltipParagraph>
        Controls the multiple used for transfer quantities. Larger sets reduce tiny movements, but
        can also prevent a transfer when no valid set multiple fits.
      </TooltipParagraph>
      <TooltipList>
        <TooltipListItem>
          <strong>Do not use:</strong> keep transfer set at the default of 1.
        </TooltipListItem>
        <TooltipListItem>
          <strong>Min. Sets from data:</strong> use MinimalSetForRedistribution from SKU attributes.
        </TooltipListItem>
        <TooltipListItem>
          <strong>Human Feasible Min. Sets:</strong> calculate the set from available-supply
          thresholds below.
        </TooltipListItem>
        <TooltipListItem>
          <strong>Both:</strong> use the larger of data set and calculated set.
        </TooltipListItem>
      </TooltipList>
    </TooltipContent>
  ),

  supplyPreferenceModifier: (
    <TextTooltip title="Calculated set by available supply">
      Defines the calculated redistribution set by source available supply. Below the first
      threshold the set is 1; at or above each threshold, ASO uses the configured set value.
    </TextTooltip>
  ),

  additionalOneStepAdjustment: (
    <TooltipContent>
      <TooltipTitle>One-step adjustment</TooltipTitle>
      <TooltipParagraph>Adds an extra cap to one source-target transfer step.</TooltipParagraph>
      <TooltipList>
        <TooltipListItem>
          <strong>None:</strong> no extra one-step cap.
        </TooltipListItem>
        <TooltipListItem>
          <strong>Max Picking Positions for one SKU:</strong> for forced-to-zero sources, sizes each
          step so the SKU can be depleted in roughly this many picking positions.
        </TooltipListItem>
        <TooltipListItem>
          <strong>Use Set as Max Quantity for one step:</strong> caps each transfer step to the
          redistribution set.
        </TooltipListItem>
      </TooltipList>
    </TooltipContent>
  ),

  maxPickingPositionsForOneSku: (
    <TextTooltip title="Forced-to-zero picking positions">
      Used only for forced-to-zero source SKUs. ASO increases each transfer step enough to deplete
      the source within this number of picking positions.
    </TextTooltip>
  ),

  specialPeriodMode: (
    <TooltipContent>
      <TooltipTitle>Special period</TooltipTitle>
      <TooltipParagraph>
        Lets a recent month window or exact date range influence sales demand more strongly than
        regular historical sales.
      </TooltipParagraph>
      <TooltipList>
        <TooltipListItem>
          <strong>None:</strong> use regular sales history only.
        </TooltipListItem>
        <TooltipListItem>
          <strong>Months:</strong> use application date minus the selected number of months through
          application date.
        </TooltipListItem>
        <TooltipListItem>
          <strong>Date range:</strong> use the selected explicit sales period.
        </TooltipListItem>
      </TooltipList>
    </TooltipContent>
  ),

  specialPeriodMonths: (
    <TextTooltip title="Special period months">
      Number of months before application date to use as the special sales period.
    </TextTooltip>
  ),

  specialPeriodRange: (
    <TextTooltip title="Special period date range">
      Explicit sales period to blend into the ASO demand estimate.
    </TextTooltip>
  ),

  specialPeriodWeight: (
    <TooltipContent>
      <TooltipTitle>Special period weight</TooltipTitle>
      <TooltipParagraph>
        Blend weight for special-period sales. 1 means the special period fully drives adjusted
        monthly demand; lower values mix it with regular sales.
      </TooltipParagraph>
    </TooltipContent>
  ),

  routeValueConstraints: (
    <TooltipContent>
      <TooltipTitle>Route value constraints</TooltipTitle>
      <TooltipParagraph>
        Route value is quantity multiplied by source SKU stock price. Empty values disable these
        constraints.
      </TooltipParagraph>
    </TooltipContent>
  ),

  routeValueToMerge: (
    <TextTooltip title="Minimum route value to merge">
      After optimization, ASO tries to move picking positions from routes below this value into
      other routes from the same source and department.
    </TextTooltip>
  ),

  routeValueToRemove: (
    <TextTooltip title="Minimum route value to remove">
      After merge attempts, ASO removes routes still below this value and reverses their quantities
      from the result.
    </TextTooltip>
  ),

  preferExistingRoute: (
    <TextTooltip title="Prefer existing route">
      Multiplies target score when a source warehouse already has a route to that target warehouse,
      encouraging ASO to consolidate into existing routes. 1 is neutral.
    </TextTooltip>
  ),

  preferExistingPickingPosition: (
    <TextTooltip title="Prefer existing picking position">
      Multiplies target score when the same source SKU already ships to the same target SKU,
      encouraging ASO to add to an existing picking position. 1 is neutral.
    </TextTooltip>
  ),

  maxRouteCount: (
    <TextTooltip title="Maximum route count">
      Limits outgoing ASO routes per source store. Empty or 0 disables the limit.
    </TextTooltip>
  ),

  storeTierConfiguration: (
    <TextTooltip title="Configuration by store tiers">
      Tier-specific values override Default values. Blank tier cells inherit Default; ASO requires
      every effective tier value to be resolvable.
    </TextTooltip>
  ),

  storeTierMultiplier: (
    <TextTooltip title="Tier multiplier">
      Store-tier demand multiplier. Higher values protect and fill the tier more; lower values make
      the tier easier to deplete.
    </TextTooltip>
  ),

  balancingMinimumInventoryMonths: (
    <TextTooltip title="Balancing minimum months">
      Months of adjusted demand used as the stock floor. Higher values keep more inventory at stores
      in this tier.
    </TextTooltip>
  ),

  balancingMedianInventoryMonths: (
    <TextTooltip title="Balancing median months">
      Months of adjusted demand used for the main ASO score. Higher values make stores in this tier
      more likely to receive or keep stock.
    </TextTooltip>
  ),

  balancingMaximumInventoryMonths: (
    <TextTooltip title="Balancing maximum months">
      Months of adjusted demand used as the receiving cap. Higher values let stores in this tier
      accept more stock.
    </TextTooltip>
  ),

  tier1Percentage: (
    <TextTooltip title="Tier 1 percentage">
      Percentage of highest-value target stores assigned to Tier 1 when tiers are recomputed.
    </TextTooltip>
  ),

  tier2Percentage: (
    <TextTooltip title="Tier 2 percentage">
      Percentage of next highest-value target stores assigned to Tier 2 when tiers are recomputed.
      Remaining stores become Tier 3.
    </TextTooltip>
  ),

  recomputeStoreTiers: (
    <TextTooltip title="Recompute store tiers">
      Rebuilds computed tiers from the last 3 months of target-SKU sales value, using application
      date as the reference date.
    </TextTooltip>
  ),

  forceTier: (
    <TextTooltip title="Force tier">
      Overrides the computed tier for this store. The forced tier is what ASO uses during
      computation.
    </TextTooltip>
  ),

  setAllStores: (
    <TextTooltip title="Set all stores">
      Forces every listed store to the selected tier.
    </TextTooltip>
  ),

  resetAllStores: (
    <TextTooltip title="Reset all stores">
      Clears forced tiers so stores use their computed tiers again.
    </TextTooltip>
  ),

  globalSourceMinLayer: (
    <TextTooltip title="Global source min layer">
      Applies source keep quantities across the scenario. Source SKUs normally cannot be reduced
      below the matching min-layer quantity.
    </TextTooltip>
  ),

  globalTargetMinLayer: (
    <TextTooltip title="Global target min layer">
      Applies target fill quantities across the scenario. Matching target SKUs are prioritized until
      they reach the min-layer quantity.
    </TextTooltip>
  ),

  scopeSourceMinLayer: (
    <TextTooltip title="Scope source min layers">
      Scope-specific keep quantities for source SKUs. If multiple source layers match a SKU, ASO
      uses the highest quantity.
    </TextTooltip>
  ),

  scopeTargetMinLayer: (
    <TextTooltip title="Scope target min layers">
      Scope-specific fill quantities for target SKUs. If multiple target layers match a SKU, ASO
      uses the highest quantity.
    </TextTooltip>
  ),

  scope: (
    <TextTooltip title="ASO scope">
      Groups one source and its priority targets. Each source and target links to a reusable Scope
      that defines its SKUs.
    </TextTooltip>
  ),

  forcedSource: (
    <TextTooltip title="Forced">
      Pushes selected source SKUs out more aggressively while still respecting any source min layer.
    </TextTooltip>
  ),

  forcedToZeroSource: (
    <TextTooltip title="Forced to zero">
      Strongly prioritizes selected source SKUs for depletion down to their source min layer,
      usually zero.
    </TextTooltip>
  ),
} satisfies Record<string, React.ReactNode>;

export type AsoTooltipKey = keyof typeof asoTooltips;
